import {Component, Input} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {finalize} from "rxjs/operators";
import {BsModalRef} from "ngx-bootstrap/modal";
import {environment} from "../../../../environments/environment";
import {ToastService} from "../../../main/bootstrap/toast/ToastService";
import {AuthenticationService} from "../../../core";

@Component({
  selector: 'app-enable-temporary-otp-modal',
  templateUrl: './enable-temporary-otp-modal.component.html',
  styleUrls: ['./enable-temporary-otp-modal.component.css']
})
export class EnableTemporaryOTPModalComponent {
  @Input() props?: {
    request: any,
    email?: string,
    isPasswordReset?: boolean,
    returnUrl: string
  }

  step: 'INFO' | 'QR_SCAN' | 'OTP' | 'SUCCESS' = 'INFO';

  qrCode?:SafeResourceUrl;
  secretKey = '';

  showSecretKey = false;

  otp = '';

  working = false;

  userInfo?:any;

  response:any;

  constructor(
    private auth: AuthenticationService,
    private toast: ToastService,
    private modalRef: BsModalRef,
    private router: Router,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
  ) {}

  getQRCode(){
    if (!this.props?.isPasswordReset){
      this.toast.showDanger('Please reset your password before setting up Two-step verification');
      return;
    }

    this.working = true;
    this.auth.setupOtp({
      authType: 'USERNAME',
      value: this.props.request.username,
      password:this.props.request.password,
    })
      .pipe(finalize(() => { this.working = false; }))
      .subscribe({
        next: (res:any) => {
          const qrCodeBase64 = res?.data?.qrCodeBase64;
          this.secretKey = res?.data?.secret;

          const {file} = this.base64ToBlob(qrCodeBase64);
          this.qrCode = file;

          this.step = 'QR_SCAN';
        },
        error: (err) => {
          this.toast.showDanger(err?.error?.message || 'Failed to get QR code!');
        }
      })
  }

  toggleSecretKey(){
    this.showSecretKey = !this.showSecretKey;
  }

  verifyOtp(){
    if (!this.otp?.length || (this.otp?.length < 6)) {
      this.toast.showWarning('Please enter a valid OTP');
      return;
    }

    this.working = true;
    this.auth.verifyOtp({
      authType: 'USERNAME',
      value: this.props?.request.username,
      code: this.otp,
    })
      .pipe(finalize(() => { this.working = false; }))
      .subscribe({
        next: (res) => {
          this.response = res;
          this.userInfo = res.data?.portalUser;
          this.step = 'SUCCESS';
        },
        error: (err) => {
          this.toast.showDanger(err?.error?.message || 'Invalid or expired token!');
        }
      })
  }

  getRedactedEmail(){
    const emailArr = this.userInfo?.email?.split('@');

    const firstHalf = emailArr?.[0] || '';
    const secondHalf = emailArr?.[1] || '';

    const newEmail = (firstHalf.split('').map((str, idx) => {
      return ((idx === 0) ||  (idx === (firstHalf.length - 1))) ? str : '*'
    })).join('');

    return `${newEmail}@${secondHalf}`;
  }

  async copySecretKey(){
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(this.secretKey)
        this.toast.showInfo('Copied to clipboard!');

      } else {
        throw new Error('Failed to Copied to clipboard!');
      }
    } catch (error) {
      // Fallback to execCommand if Clipboard API is not supported
      const textarea = document.createElement('textarea');
      textarea.value = this.secretKey;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');

      this.toast.showInfo('Copied to clipboard!');

      document.body.removeChild(textarea);
    }
  }


  getLandingPage(response: any) {
    let roles = this.auth.getGrantedAuthority(response);
    let landing = "/dashboard";
    if (roles.includes("CAC_CRP_DATA_ENTRY_OFFICER")) {
      landing = "/dashboard";
    } else {
      landing = "/company-registration/company-list";
    }
    return landing;
  }

  proceed() {
    const landingPage = this.getLandingPage(this.response);
    const returnUrl = this.route.snapshot.queryParams?.redirect || landingPage
    this.router.navigateByUrl(returnUrl);
    this.modalRef.hide();
    // this.router.navigate(['/dashboard'])
  }

  goToPasswordReset(){
    window.location.href = environment.passwordResetURL;
    // this.router.navigateByUrl('/login/passwordChange');
    this.modalRef.hide();
  }

  private base64ToBlob(base64: string) {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    const contentType = this.detectMimeType(base64);

    const type = contentType.includes('image') ? 'image' : contentType.includes('pdf') ? 'pdf' : 'file';

    const blob = new Blob([byteArray], { type: contentType });
    const url = URL.createObjectURL(blob);
    const file = this.sanitizer.bypassSecurityTrustResourceUrl(url);

    return { blob, file, type };
  }

  private detectMimeType(base64: string): string {
    const bytes = this.base64ToUint8Array(base64);
    const header = bytes.slice(0, 8).reduce((acc, byte) => acc + byte.toString(16).padStart(2, '0'), '');

    if (header.startsWith('25504446')) {
      return 'application/pdf'; // PDF
    }
    if (header.startsWith('ffd8ff')) {
      return 'image/jpeg';
    }
    if (header.startsWith('89504e47')) {
      return 'image/png';
    }
    if (header.startsWith('47494638')) {
      return 'image/gif';
    }
    if (header.startsWith('504b0304')) {
      return 'application/zip'; // could be DOCX/XLSX/PPTX
    }

    return 'application/octet-stream'; // Unknown binary
  }

  private base64ToUint8Array(base64: string): Uint8Array {
    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}
